import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type OutletResource,
  OutletsController_rename_v1,
  type OutletsController_rename_v1Body,
  OutletsController_retire_v1,
  type TaxIdentityResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { Switch } from '@repo/ui/components/switch';
import { IconAlertTriangle } from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { outletStockRefusal } from '../catalogue/outlet-stock-refusal';
import { isRetired, labelForKind, labelForStock } from './outlet-kinds';
import { labelForTaxIdentity } from './registrations';
import { RegistrationSelect } from './RegistrationSelect';

/**
 * One outlet, opened from the list: `PATCH /v1/outlets/{id}` and
 * `DELETE /v1/outlets/{id}`.
 *
 * **`code` and `kind` are not offered.** `UpdateOutletDto` carries `name` and
 * `carriesStock` and nothing else, and the API says why in the document itself:
 * both are immutable once set (FR-114, CL-103). A form that rendered them as
 * disabled fields would be a form that looks broken; one that rendered them as
 * live fields and then surfaced a refusal would be worse. They are shown as what
 * they are — the outlet's identity — with one line saying why they hold still,
 * so their absence reads as a rule rather than as a missing feature.
 *
 * **Retiring is a soft delete and the copy has to carry that.** The confirmation
 * is the only place a person is told that sales, tickets and audit entries made
 * at this outlet survive it — somebody hesitating over a button that might
 * destroy the park's history deserves to know it does not, before they press it
 * rather than after. It also names the one real consequence they would otherwise
 * discover by accident: the code comes back into circulation, which is what
 * `IDENTITY_OUTLET_CODE_TAKEN`'s own copy already promises.
 *
 * **A retired outlet still opens.** History stays reachable, so the row remains
 * openable from the include-retired view — read-only, because the API answers
 * both `PATCH` and `DELETE` on a retired outlet with `RESOURCE_NOT_FOUND` and a
 * screen offering controls that cannot work is a screen teaching staff to
 * distrust it.
 *
 * The confirmation is a stage of this dialog rather than a second one on top of
 * it. Two stacked dialogs put the focus trap inside another focus trap for a
 * question that is about the thing already open, and the registry has no
 * alert-dialog here to do it properly.
 */

const NAME_REQUIRED = 'Enter a name for this outlet.';

/**
 * What this dialog will send.
 *
 * Two fields, because `UpdateOutletDto` has two. There is no `code` and no
 * `kind` here for the same reason there is none on the DTO — see the note above.
 */
const editOutletSchema = z.object({
  name: z.string().trim().min(1, NAME_REQUIRED),
  carriesStock: z.boolean(),
  // `''` only for an outlet that has never named one, and left unsent then:
  // `UpdateOutletDto` takes a registration, never a null.
  taxIdentityId: z.string(),
});

type EditOutletValues = z.infer<typeof editOutletSchema>;

/** Which question the dialog is currently asking. One value, so they cannot overlap. */
type Stage = 'editing' | 'confirming-retire';

export function EditOutletDialog({
  outlet,
  taxIdentities,
  onOpenChange,
  onUpdated,
  onRetired,
}: {
  outlet: OutletResource;
  taxIdentities: readonly TaxIdentityResource[];
  onOpenChange: (open: boolean) => void;
  onUpdated: (outlet: OutletResource) => void;
  onRetired: (outlet: OutletResource) => void;
}) {
  const retired = isRetired(outlet);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <FormDialogContent>
        {retired ? (
          <RetiredOutlet outlet={outlet} taxIdentities={taxIdentities} />
        ) : (
          // Mounted per open, like the create form, so a half-typed rename and
          // the refusal about it do not wait for the next person to open it.
          <EditableOutlet
            outlet={outlet}
            taxIdentities={taxIdentities}
            onUpdated={onUpdated}
            onRetired={onRetired}
          />
        )}
      </FormDialogContent>
    </Dialog>
  );
}

/**
 * What identifies the outlet, and does not change.
 *
 * Values in the same faces they carry everywhere else — the code is read aloud
 * and typed back in, so it keeps the mono face and tabular figures it has in the
 * table.
 */
function Identity({ outlet }: { outlet: OutletResource }) {
  return (
    // The one contrast trap in this system: `text-muted-foreground` on
    // `bg-muted` measures 4.39:1 and misses AA. The values here are
    // `text-foreground` for that reason; the labels take the signage class,
    // which is already `muted-foreground` and is used at label size on the
    // page ground everywhere else in the app.
    <div className="flex flex-wrap gap-x-10 gap-y-4 rounded-2xl bg-muted px-4 py-3">
      <div className="space-y-1.5">
        <p className={fieldLabel}>Code</p>
        <p className="font-mono text-foreground numeric">{outlet.code}</p>
      </div>
      <div className="space-y-1.5">
        <p className={fieldLabel}>Kind</p>
        <p className="text-foreground">{labelForKind(outlet.kind)}</p>
      </div>
    </div>
  );
}

function EditableOutlet({
  outlet,
  taxIdentities,
  onUpdated,
  onRetired,
}: {
  outlet: OutletResource;
  taxIdentities: readonly TaxIdentityResource[];
  onUpdated: (outlet: OutletResource) => void;
  onRetired: (outlet: OutletResource) => void;
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(editOutletSchema),
    mode: 'onTouched',
    defaultValues: {
      name: outlet.name,
      carriesStock: outlet.carriesStock,
      taxIdentityId: outlet.taxIdentityId ?? '',
    },
  });

  const [stage, setStage] = useState<Stage>('editing');

  // A `ReactNode` rather than a `string`, for one refusal: the stock one names
  // the items still sold here and links to them, and a sentence with a link in it
  // is not a string. Everything else that sets this is still the message
  // `messageForFailure` returns, which is a `ReactNode` too.
  const [formError, setFormError] = useState<ReactNode>(null);

  // One key for as long as this form is open. The dialog unmounts on success, so
  // the next attempt gets a fresh one; a refusal rolls the claim back
  // server-side, so a corrected resubmit re-claims cleanly. Where this component
  // makes more than one kind of write, the same key is safe across them: the
  // API's record is unique on `(scope, key)`, and each of those writes is its own
  // scope.
  const [idempotencyKey] = useState(newIdempotencyKey);

  const [retiring, setRetiring] = useState(false);

  const pending = form.formState.isSubmitting || retiring;

  // `isDirty` rather than a hand-written comparison: the defaults above are the
  // outlet as it stands, so react-hook-form is already answering exactly this
  // question and a second answer could disagree with it.
  const changed = form.formState.isDirty;

  const handleSubmit = async ({ name, carriesStock, taxIdentityId }: EditOutletValues) => {
    if (pending) return;

    setFormError(null);

    try {
      // The shape comes from the generated client, and it is the shape that
      // makes the immutability structural: there is no `code` and no `kind` on
      // `UpdateOutletDto`, so this file could not send them if it wanted to.
      //
      // The registration is sent only when it changed. Unchanged, it is not
      // this save's business, and an outlet with tills keeps it regardless.
      const body: OutletsController_rename_v1Body = {
        name,
        carriesStock,
        ...(taxIdentityId !== '' && taxIdentityId !== outlet.taxIdentityId
          ? { taxIdentityId }
          : {}),
      };

      onUpdated(
        await client.request(OutletsController_rename_v1, {
          path: { id: outlet.id },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 'IDENTITY_OUTLET_TAX_IDENTITY_FIXED') {
        // About one field, so it goes beside that field.
        form.setError('taxIdentityId', { message: messageForFailure(error) });
        return;
      }

      // The fourth door into the pair FR-208 forbids, and the only one that is
      // not on a catalogue screen: turning stock off here is refused while this
      // outlet still sells a stock-tracked item. The generic copy for the code
      // is about outlets somebody chose, and nobody chose any outlets on this
      // form — so `outletStockRefusal` names the items and where they are
      // withdrawn. It falls back to that copy on any failure of its own.
      setFormError(
        error instanceof ApiError && error.code === 'CATALOGUE_STOCK_NOT_AVAILABLE_HERE'
          ? await outletStockRefusal(client, outlet, error)
          : // Nothing else here is about one field — a lapsed session, a role
            // that does not permit it, an outlet somebody else retired while
            // this was open — so it goes above the submit, where it stays until
            // it is dealt with.
            messageForFailure(error),
      );
    }
  };

  const handleRetire = async () => {
    if (pending) return;

    setFormError(null);
    setRetiring(true);

    try {
      onRetired(
        await client.request(OutletsController_retire_v1, {
          path: { id: outlet.id },
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error) {
      setFormError(messageForFailure(error));

      // Back to the form, because the confirmation's copy is about a thing that
      // did not happen and leaving it up alongside a refusal reads as though it
      // half did.
      setStage('editing');
    } finally {
      setRetiring(false);
    }
  };

  if (stage === 'confirming-retire') {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Retire {outlet.name}?</DialogTitle>
          <DialogDescription>Retiring is not deleting.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm text-foreground">
          <p>
            Sales, tickets and audit entries made at this outlet stay reachable for ever. Nothing
            that happened here is lost, and past days still reconcile.
          </p>
          <p>
            It leaves the outlets list unless retired ones are shown, no terminal can be stood in it
            again, and its code{' '}
            <span className="font-mono text-foreground numeric">{outlet.code}</span> becomes
            available for a new outlet.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={pending} onClick={() => setStage('editing')}>
            Keep it trading
          </Button>
          <Button variant="destructive" disabled={pending} onClick={() => void handleRetire()}>
            {pending ? 'Retiring…' : 'Yes, retire it'}
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{outlet.name}</DialogTitle>
        <DialogDescription>
          What this outlet is called, whether it holds stock, and whose GSTIN its tills print.
        </DialogDescription>
      </DialogHeader>

      {/* One column, per docs/ui-conventions.md. `noValidate` because the
          browser's own bubbles are not this app's error language. */}
      <Form {...form}>
        <form
          className="space-y-5"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          <Identity outlet={outlet} />

          <p className="text-sm text-muted-foreground">
            A code and a kind cannot be changed once the outlet exists. A shop that becomes an
            activity centre is a different place with its own history, so it is created as a new
            outlet rather than edited into one.
          </p>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Name
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11`}
                      required
                      placeholder="Doll Shop"
                      autoComplete="off"
                      {...field}
                    />
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="carriesStock"
            render={({ field }) => (
              <div className="flex items-start gap-3 rounded-2xl bg-muted px-4 py-3">
                <Switch
                  id="carries-stock"
                  aria-labelledby="carries-stock-label"
                  className="mt-0.5"
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(checked)}
                />
                <div className="space-y-1">
                  <Label
                    id="carries-stock-label"
                    htmlFor="carries-stock"
                    className="cursor-pointer text-sm text-foreground"
                  >
                    Holds stock
                  </Label>
                  <p className="text-sm text-foreground/70">
                    Off for an outlet that takes money but sells nothing off a shelf. Separate from
                    the kind, because an outlet need not follow its kind. Turning it off is refused
                    while this outlet still sells an item that counts stock — withdraw those items
                    from it first.
                  </p>
                </div>
              </div>
            )}
          />

          <FormField
            control={form.control}
            name="taxIdentityId"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className={fieldLabel}>GST registration</FormLabel>
                {/* Not wrapped in `FormControl`: the trigger is a button with its
                    own accessible name, described by the message by hand. */}
                <RegistrationSelect
                  id="outlet-tax-identity"
                  value={field.value}
                  onChange={field.onChange}
                  identities={taxIdentities}
                  errorId={fieldState.error === undefined ? undefined : 'outlet-tax-identity-error'}
                />
                <FormDescription>
                  Whose GSTIN this outlet’s tills print. Fixed once the outlet has a till, so that
                  no till’s invoices run under two GSTINs.
                </FormDescription>
                <FormMessage id="outlet-tax-identity-error" />
              </FormItem>
            )}
          />

          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="sm:justify-between">
            {/* Not the screen's accent and not a row control: the way out of an
                outlet's life sits at the far end of its own form, where it is
                found deliberately rather than brushed past. */}
            <Button
              type="button"
              variant="outline"
              className="text-destructive"
              disabled={pending}
              onClick={() => setStage('confirming-retire')}
            >
              Retire outlet
            </Button>

            {/* Disabled until something differs. Every mutating request writes
                an audit entry, and a PATCH that changes nothing is a row in the
                park's history saying somebody opened a dialog. */}
            <Button type="submit" disabled={pending || !changed}>
              {pending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}

/**
 * A retired outlet, open and read-only.
 *
 * Everything it said while it was trading, still legible — the whole reason the
 * row is reachable is that somebody is looking something up — and no control
 * that the API would refuse.
 */
function RetiredOutlet({
  outlet,
  taxIdentities,
}: {
  outlet: OutletResource;
  taxIdentities: readonly TaxIdentityResource[];
}) {
  const registration = taxIdentities.find((identity) => identity.id === outlet.taxIdentityId);

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex flex-wrap items-center gap-3">
          {outlet.name}
          {/* `outline` and not `destructive`, the same as in the list. The end
              of an outlet's life is an ordinary event, and red is kept for a
              system that refused something or is about to. */}
          <Badge variant="outline">Retired</Badge>
        </DialogTitle>
        <DialogDescription>Kept for history. Nothing here can be changed.</DialogDescription>
      </DialogHeader>

      <Identity outlet={outlet} />

      <div className="space-y-1.5">
        <p className={fieldLabel}>Name</p>
        <p className="text-sm text-foreground">{outlet.name}</p>
      </div>

      <div className="space-y-1.5">
        <p className={fieldLabel}>Stock</p>
        <p className="text-sm text-foreground">{labelForStock(outlet.carriesStock)}</p>
      </div>

      <div className="space-y-1.5">
        <p className={fieldLabel}>GST registration</p>
        <p className="text-sm text-foreground">
          {registration === undefined ? 'None' : labelForTaxIdentity(registration)}
        </p>
      </div>

      <p className="text-sm text-muted-foreground">
        Sales, tickets and audit entries made here stay reachable for ever, which is why this still
        opens. The code above has been released and another outlet may hold it now.
      </p>
    </>
  );
}
